import React, { useRef, useState } from "react";
import { Camera } from "lucide-react";
import { cn } from "@/lib/utils";

// Compress an image file to a JPEG data URL (max 600px, quality 0.8)
export function compressImage(file, maxSize = 600, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > height && width > maxSize) {
          height = Math.round((height * maxSize) / width);
          width = maxSize;
        } else if (height > maxSize) {
          width = Math.round((width * maxSize) / height);
          height = maxSize;
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function initials(name) {
  return (name || "?")
    .split(" ")
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

// Avatar with optional click-to-upload behaviour
export default function AvatarUpload({
  name,
  photo,
  onUpload, // async (dataUrl) => void  — if omitted, avatar is display-only
  size = "md",
  testId,
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const sizes = {
    sm: "h-9 w-9 text-xs",
    md: "h-12 w-12 text-sm",
    lg: "h-20 w-20 text-lg",
    xl: "h-28 w-28 text-2xl",
  };

  const pick = () => {
    if (onUpload && !busy) inputRef.current?.click();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const dataUrl = await compressImage(file);
      await onUpload(dataUrl);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative inline-block shrink-0">
      <button
        type="button"
        onClick={pick}
        data-testid={testId}
        className={cn(
          "relative grid place-items-center overflow-hidden rounded-full bg-primary/10 font-semibold text-primary ring-1 ring-border/60",
          sizes[size],
          onUpload ? "cursor-pointer transition-opacity hover:opacity-80" : "cursor-default",
        )}
      >
        {photo ? (
          <img src={photo} alt={name} className="h-full w-full object-cover" />
        ) : (
          <span>{initials(name)}</span>
        )}
        {busy && (
          <span className="absolute inset-0 grid place-items-center bg-black/40 text-[10px] text-white">…</span>
        )}
      </button>
      {onUpload && (
        <span className="pointer-events-none absolute -bottom-0.5 -right-0.5 grid h-5 w-5 place-items-center rounded-full bg-primary text-primary-foreground shadow">
          <Camera className="h-3 w-3" />
        </span>
      )}
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onFile} />
    </div>
  );
}
